import { AGENT_TYPE } from '../actions/agent'
import { getIceInfo,
  notifyAgentAddress,
  notifyIceServers,
  resolveAgentAddress,
  resolveInstalledAgentAddress,
  sendOffer,
  getUserDetailsFromTokenRequest,
  notifyAgentDescription,
  notifyNrcErrorResponse,
  notifyNoPermission,
  notifyConnectionLimitReached
  , sendIceCandidate } from '../actions/signaling'
import { sendWssMessage, parseWssMessage } from '../actions/wss'
import { ProtobufMessageCreator } from './create/ProtobufMessageCreator'
import { RouteMessageCreator } from './create/RouteMessageCreator'
import { TransportMessageCreator } from './create/TransportMessageCreator'
import { ResponseMessageParser } from './parse/ResponseMessageParser'
import { RouteMessageParser } from './parse/RouteMessageParser'
import { TransportMessageParser } from './parse/TransportMessageParser'
import { raiseError, setDisconnectMessage, gracefulExit } from '../actions/disconnect'
import { createPermissionsInstance } from './Permissions'
import { logger } from './utils/logger'

const NO_PERMISSION = 'NO_PERMISSION'
const CONNECTION_LIMIT = 'CONNECTION_LIMIT'

export const createSignalingMiddleware = ({ responseMessageParser, messageCreator }) => {
  return () => {
    return (store) => {
      function sendWssGetIceInfoRequest () {
        const msgBinary = messageCreator.createGetIceInfoRequest()
        store.dispatch(sendWssMessage(msgBinary))
      }

      function sendWssResolveAgentAddressRequest (sessionKey) {
        const msg = messageCreator.createResolveAgentAddressRequest(sessionKey)
        store.dispatch(sendWssMessage(msg))
      }

      function sendWssResolveInstalledAgentAddressRequest (id) {
        const msg = messageCreator.createResolveInstalledAgentAddressRequest(id)
        store.dispatch(sendWssMessage(msg))
      }

      function sendWssGetUserDetailsFromTokenRequest (token) {
        const msg = messageCreator.createGetUserDetailsFromTokenRequest(token)
        store.dispatch(sendWssMessage(msg))
      }

      function checkIfCanCreateOffer (payload) {
        const storeContent = {
          ...store.getState().signaling,
          ...payload
        }

        if (storeContent.stunList && storeContent.turnList && storeContent.agentAddress) {
          logger(' - send route [AgentDetailsRequest]')
          const msg = messageCreator.createAgentDetailsRequest(storeContent.agentAddress)
          store.dispatch(sendWssMessage(msg))
        }
      }

      function onSendOffer (clientDescription) {
        logger(' - send route [Offer]')
        const token = store.getState().login.token
        const agentAddress = store.getState().signaling.agentAddress
        const offerMsg = messageCreator.createOffer(JSON.stringify(clientDescription), token, agentAddress)

        store.dispatch(sendWssMessage(offerMsg))
      }

      /**
       *
       * @param {*https://developer.mozilla.org/en-US/docs/Web/API/RTCIceCandidate} rtcIceCandidate
       */
      function onSendIceCandidate (rtcIceCandidate) {
        logger(' - send route [ICECandidates]')
        const agentAddress = store.getState().signaling.agentAddress
        const iceCandidateMsg = messageCreator.createICECandidates(rtcIceCandidate, agentAddress)

        store.dispatch(sendWssMessage(iceCandidateMsg))
      }

      function onParseWssMessage (payload) {
        const resultAction = responseMessageParser.parse(payload)
        store.dispatch(resultAction)
      }

      function triggerErrorPage (payload) {
        logger(' - ws ERROR message received', payload)
        const {
          webrtcConnection: {
            iceConnectionState,
          },
          agent: {
            unattended,
          }
        } = store.getState()

        let errorCode = (payload && payload.code) ? payload.code : null
        let message = null
        switch (errorCode) {
          case '5301': // AGENT_SESSION_KEY_NOT_FOUND
            message = 'error-invalid-key'
            break
          case '70001': // DESTINATION_NOT_REACHABLE
            // for installed agent (device), there is no DEVICE_NOT_FOUND error;
            // if the device does not exists the device is not connected
            if (iceConnectionState === 'connected') {
              logger('  - ws error DESTINATION_NOT_REACHABLE ignored, webRTC is already connected')
            } else {
              message = 'error-agent-not-connected'
            }
            break
          case '70002': // invalid account
            message = 'error-connecting'
            break
          default:
            break
        }
        if (message) {
          if (unattended) {
            message = `${message}-unattended`
          }
          store.dispatch(raiseError({ message, error: payload.name }))
        }
      }

      function onAgentDescription ({ permissions }) {
        createPermissionsInstance({
          agentType: store.getState().agent.type,
          permissions,
        })
      }

      function checkIfConnectedToPortal (payload) {
        if (payload.agentAddress) {
          checkIfCanCreateOffer(payload)
        } else {
          const { agent: { type, unattended } } = store.getState()
          let message = 'error-agent-not-connected'
          if (unattended) {
            message = `${message}${(type === AGENT_TYPE.CLASSROOM_AGENT) ? '-classroom' : '-unattended'}`
          }
          store.dispatch(raiseError(message))
        }
      }

      function onOfferRejected (payload) {
        const unattended = store.getState().agent.type === AGENT_TYPE.INSTALLED_AGENT
        store.dispatch(gracefulExit())
        let message = null
        switch (payload) {
          case NO_PERMISSION:
            message = 'disconnect-no-permissions'
            break
          case CONNECTION_LIMIT:
            message = 'disconnect-already-running'
            break
          default:
            break
        }
        if (message) {
          message = unattended ? `${message}-unattended` : message
          store.dispatch(setDisconnectMessage(message))
        }
      }

      return (next) => (action) => {
        const actions = {
          [getIceInfo]: () => sendWssGetIceInfoRequest(),
          [resolveAgentAddress]: (payload) => sendWssResolveAgentAddressRequest(payload),
          [resolveInstalledAgentAddress]: (payload) => sendWssResolveInstalledAgentAddressRequest(payload),
          [getUserDetailsFromTokenRequest]: (payload) => sendWssGetUserDetailsFromTokenRequest(payload),
          [notifyIceServers]: (payload) => checkIfCanCreateOffer(payload),
          [notifyAgentAddress]: (payload) => checkIfConnectedToPortal(payload),
          [sendOffer]: (payload) => onSendOffer(payload),
          [sendIceCandidate]: (payload) => onSendIceCandidate(payload),
          [notifyNrcErrorResponse]: (payload) => triggerErrorPage(payload),
          [notifyAgentDescription]: (payload) => onAgentDescription(payload),
          [notifyNoPermission]: () => onOfferRejected(NO_PERMISSION),
          [notifyConnectionLimitReached]: () => onOfferRejected(CONNECTION_LIMIT),
          [parseWssMessage]: (payload) => onParseWssMessage(payload)
        }

        if (actions.hasOwnProperty(action.type)) {
          actions[action.type](action.payload)
        }

        return next(action)
      }
    }
  }
}

const deps = {
  responseMessageParser: new ResponseMessageParser(new TransportMessageParser(new RouteMessageParser())),
  messageCreator: new ProtobufMessageCreator(new TransportMessageCreator(), new RouteMessageCreator())
}

export const signalingMiddleWare = createSignalingMiddleware(deps)
